import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Users, TrendUp, CalendarDots, Briefcase } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, candidatesRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/candidates`),
        axios.get(`${API}/dashboard/weekly-activity`)
      ]);
      
      setStats(statsRes.data);
      setRecentCandidates(candidatesRes.data.slice(0, 5));
      setWeeklyData(weeklyRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="stat-card p-6 h-32 skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Candidates',
      value: stats?.total_candidates || 0,
      icon: Users,
      color: 'text-[#002FA7]',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Job Descriptions',
      value: stats?.total_jobs || 0,
      icon: Briefcase,
      color: 'text-[#0284C7]',
      bgColor: 'bg-sky-50',
    },
    {
      label: 'Parsed Today',
      value: stats?.parsed_today || 0,
      icon: CalendarDots,
      color: 'text-[#059669]',
      bgColor: 'bg-emerald-50',
    },
    {
      label: 'Growth',
      value: stats?.parsed_today > 0 ? `+${stats.parsed_today}` : '—',
      icon: TrendUp,
      color: 'text-[#D97706]',
      bgColor: 'bg-amber-50',
    },
  ];

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Overview of your CV management system
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="stat-card p-6"
                data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium tracking-wide text-slate-500 uppercase mb-2">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-md`}>
                    <Icon size={24} weight="duotone" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-medium tracking-tight text-slate-800 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Weekly Activity
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(200, 46, 235, 0.15)" />
                <XAxis dataKey="name" stroke="#B8B0C8" />
                <YAxis stroke="#B8B0C8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 15, 60, 0.95)',
                    border: '1px solid rgba(200, 46, 235, 0.3)',
                    borderRadius: '0.5rem',
                    color: '#fff',
                  }}
                  cursor={{ fill: 'rgba(200, 46, 235, 0.08)' }}
                />
                <Bar dataKey="count" fill="#C82EEB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-medium tracking-tight text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recent
              </h2>
              <Link
                to="/candidates"
                className="text-sm font-medium text-[#002FA7] hover:text-[#00227A]"
                data-testid="view-all-link"
              >
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {recentCandidates.length === 0 ? (
                <p className="text-sm text-slate-500">No candidates yet</p>
              ) : (
                recentCandidates.map((candidate) => (
                  <Link
                    key={candidate.id}
                    to={`/candidates/${candidate.id}`}
                    className="block p-4 rounded-md border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 transition-all duration-200"
                    data-testid={`recent-candidate-${candidate.id}`}
                  >
                    <p className="text-base font-medium text-slate-900">{candidate.name || 'Unknown'}</p>
                    <p className="text-sm text-slate-600 mt-1">{candidate.role || 'No role specified'}</p>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;